import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Modal, Input, List } from 'antd';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash-es';
import { IRoute } from 'interfaces';
import { commonHelpers, trackingHelpers } from 'helpers';
import { trackingConstants } from 'constants/index';
import classnames from 'classnames';
import { t, getLazyName } from 'helpers/i18n';

interface CommandPaletteProps {
  visible: boolean;
  onClose: () => void;
  filteredNavigation: IRoute[];
}

const CommandPalette: React.FC<CommandPaletteProps> = ({
  visible,
  onClose,
  filteredNavigation,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRoutes, setFilteredRoutes] = useState<IRoute[]>(
    filteredNavigation
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const history = useHistory();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Debounced tracking function for search
  const trackSearch = useCallback((keyword: string, resultsCount: number) => {
    if (keyword.trim()) {
      trackingHelpers.nonBlockTrackCustomEvent(
        trackingConstants.TRACKING_CONTENT_NAME.CommandPaletteSearch,
        {
          action: 'search',
          category: 'staff-web-app',
          property: JSON.stringify({
            searchKeyword: keyword,
            resultsCount,
            searchLength: keyword.length,
          }),
        }
      );
    }
  }, []);

  const debouncedTrackSearch = useMemo(() => debounce(trackSearch, 800), [
    trackSearch,
  ]);

  const availRoutes = useMemo(() => {
    const collectRoutesRecursively = (
      routes: IRoute[],
      isFirstLevel = false
    ): IRoute[] => {
      const collected: IRoute[] = [];

      for (const route of routes) {
        // Only check icon requirement for first level routes
        if (isFirstLevel && !route.icon) continue;

        if (!route.children) {
          collected.push(route);
          continue;
        }

        const childRoutes = filteredNavigation.filter(child =>
          commonHelpers.checkShowMenuItem(route.children!, child)
        );
        collected.push(...collectRoutesRecursively(childRoutes, false));
      }

      return collected;
    };

    const allRoutes = collectRoutesRecursively(filteredNavigation, true);

    return allRoutes.filter(
      route => !route.path.includes(':') && !route.path.includes('*')
    );
  }, [filteredNavigation]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = availRoutes.filter(route => {
        const routeName = getLazyName(route.name);
        const accentRemovedName = commonHelpers.noAccentVietnamese(routeName);

        // Return true if searchTerm is in name (both accent and no accent)
        return (
          routeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          accentRemovedName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredRoutes(filtered);

      // Track search with debounce
      debouncedTrackSearch(searchTerm, filtered.length);
    } else {
      setFilteredRoutes(availRoutes);
    }
    setSelectedIndex(0);
  }, [searchTerm, availRoutes, debouncedTrackSearch]);

  useEffect(() => {
    if (visible) {
      setSelectedIndex(0);
    } else {
      // Cancel pending debounced tracking when modal closes
      debouncedTrackSearch.cancel();
    }
  }, [visible, debouncedTrackSearch]);

  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  const handleSelectRoute = (route: IRoute) => {
    history.push(route.path);
    onClose();
    setSearchTerm('');
    trackingHelpers.nonBlockTrackCustomEvent('navigate-command-palette', {
      action: 'navigate',
      category: 'staff-web-app',
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      setSearchTerm('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredRoutes.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(
        prev => (prev - 1 + filteredRoutes.length) % filteredRoutes.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredRoutes[selectedIndex]) {
        handleSelectRoute(filteredRoutes[selectedIndex]);
      }
    }
  };

  return (
    <Modal
      title={t('SearchMenu')}
      visible={visible}
      onCancel={onClose}
      footer={t('SearchMenuNavigationHint')}
      width={500}
      destroyOnClose
      keyboard
    >
      <Input
        placeholder={t('SearchMenuPlaceholder')}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        className="mb-base"
      />
      <List
        dataSource={filteredRoutes}
        renderItem={(route, index) => (
          <div ref={el => (itemRefs.current[index] = el)}>
            <List.Item
              onMouseEnter={() => setSelectedIndex(index)}
              onMouseLeave={() => setSelectedIndex(-1)}
              onClick={() => handleSelectRoute(route)}
              className={classnames('cursor-pointer px-half background-hover', {
                'background-hover-hovering': index === selectedIndex,
              })}
              style={{ borderRadius: 5 }}
            >
              <List.Item.Meta title={getLazyName(route.name)} />
            </List.Item>
          </div>
        )}
        style={{ maxHeight: 300, overflow: 'auto' }}
      />
    </Modal>
  );
};

export default CommandPalette;
